'use client';

import { RoomConfiguration } from '@components/RoomConfigurator';
import {
  CHECKOUT_START_DATETIME,
  SOCIAL_LOGIN_REDIRECT,
} from '@config/storageKeys';
import { usePackagesParams } from '@hooks/use-packages-params';
import useStorage from '@hooks/useStorage';
import type { ILocationResult } from '@components/searchCity';
import type { LeagueEvent } from '@interfaces/APITravel';
import { useBuildPackagesStore } from '@store/build-package-store';
import { useCheckoutStepsStore } from '@store/checkout-steps-store';
import { trackGa4PackageComponentRemoved } from '@v2/analytics/ga4';
import { links } from '@utils/navigation/links';
import { skipPackageComponent } from '@utils/skip-package-component';
import {
  Building05,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  InfoCircle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Trash02,
  User01,
  Users01,
} from '@v2/ui';
import { cn } from '@v2/utils';
import { normalizeDateToUtcNoonIso } from '@v2/utils/date';
import { setCookie } from 'cookies-next';
import { differenceInCalendarDays, format } from 'date-fns';
import Link from 'next/link';
import { useState, type ReactNode } from 'react';
import { StayDateRangeField } from './Header/BaseFilters/StayDateRangeField';
import { HotelAreaPicker } from './HotelAreaPicker';
import { useHotelSearchArea } from '../hooks/use-hotel-search-area';
import {
  coerceHotelSearchArea,
  stadiumLocationFromEvent,
  type HotelSearchArea,
} from '../hotel-search-area';
import { confirmedRooms, type RoomDraft } from '../room-prefill';

const DATE_PART = /^(\d{4})-(\d{2})-(\d{2})/;

export type StayDates = {
  hotelStartDate: string;
  hotelEndDate: string;
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  initialRooms: RoomDraft[];
  source: 'flights' | 'tickets' | 'existing';
  event?: LeagueEvent;
  onConfirm: (
    rooms: RoomConfiguration[],
    dates: StayDates,
    area: {
      area: Extract<HotelSearchArea, 'stadium' | 'downtown'>;
      location: ILocationResult | null;
    }
  ) => void;
}

function parseHotelDate(
  value: string | Date | null | undefined
): Date | undefined {
  if (!value) return undefined;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }
  const match = String(value).match(DATE_PART);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

function formatStayDate(date: Date) {
  return format(date, 'MMM d, yyyy');
}

function countLabel(count: number, singular: string, plural: string) {
  return `${count} ${count === 1 ? singular : plural}`;
}

export function RoomConfigurationPopup({
  isOpen,
  onClose,
  initialRooms,
  source,
  event,
  onConfirm,
}: Props) {
  const { eventId, eventLink, packageType } = usePackagesParams();
  const storage = useStorage();
  const hotelStartDate = useBuildPackagesStore((state) => state.hotelStartDate);
  const hotelEndDate = useBuildPackagesStore((state) => state.hotelEndDate);
  const [rooms, setRooms] = useState<RoomDraft[]>(() =>
    initialRooms.map((room) => ({ ...room, children: [...room.children] }))
  );
  const [checkIn, setCheckIn] = useState(() => parseHotelDate(hotelStartDate));
  const [checkOut, setCheckOut] = useState(() => parseHotelDate(hotelEndDate));
  const [error, setError] = useState('');
  const storedArea = useBuildPackagesStore((state) => state.hotelSearchArea);
  const { downtown } = useHotelSearchArea(event);
  const [area, setArea] = useState<
    Extract<HotelSearchArea, 'stadium' | 'downtown'>
  >(() => {
    const current = coerceHotelSearchArea(storedArea);
    return current === 'downtown' ? 'downtown' : 'stadium';
  });

  const nights =
    checkIn && checkOut ? differenceInCalendarDays(checkOut, checkIn) : 0;
  const totals = rooms.reduce(
    (sum, room) => ({
      adults: sum.adults + room.adults,
      children: sum.children + room.children.length,
      infants: sum.infants + room.infants,
    }),
    { adults: 0, children: 0, infants: 0 }
  );
  const description =
    source === 'flights'
      ? 'We filled this in from your flight. Change the dates or rooms if you need to — this helps us show the right prices.'
      : source === 'tickets'
        ? 'We filled this in from your tickets. Change the dates or rooms if you need to — this helps us show the right prices.'
        : 'Choose your hotel dates and who is staying in each room. This helps us show the right prices.';

  const updateRoom = (index: number, update: Partial<RoomDraft>) => {
    setRooms((current) =>
      current.map((room, roomIndex) =>
        roomIndex === index ? { ...room, ...update } : room
      )
    );
  };

  const confirm = () => {
    const stayStart = checkIn;
    const stayEnd = checkOut;
    if (!stayStart || !stayEnd) {
      setError('Choose a check-in and check-out date so we can search hotels.');
      return;
    }
    if (differenceInCalendarDays(stayEnd, stayStart) < 1) {
      setError('Check-out needs to be the day after check-in or later.');
      return;
    }
    const configuration = confirmedRooms(rooms);
    if (!configuration) {
      setError('Choose an age for each child before searching.');
      return;
    }
    try {
      const nextArea = downtown && area === 'downtown' ? 'downtown' : 'stadium';
      const stadium =
        stadiumLocationFromEvent(event) ??
        useBuildPackagesStore.getState().stadiumLocation ??
        useBuildPackagesStore.getState().location ??
        null;
      onConfirm(
        configuration,
        {
          hotelStartDate: normalizeDateToUtcNoonIso(stayStart),
          hotelEndDate: normalizeDateToUtcNoonIso(stayEnd),
        },
        {
          area: nextArea,
          location: nextArea === 'downtown' ? downtown : stadium,
        }
      );
    } catch {
      setError('We couldn’t save your stay. Please try again.');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        onPointerDownOutside={(event) => {
          const target = event.target as HTMLElement | null;
          if (target?.closest('[data-radix-popper-content-wrapper]')) {
            event.preventDefault();
          }
        }}
        onFocusOutside={(event) => {
          const target = event.target as HTMLElement | null;
          if (target?.closest('[data-radix-popper-content-wrapper]')) {
            event.preventDefault();
          }
        }}
        className="inset-0 flex h-[100dvh] max-h-[100dvh] w-full translate-x-0 translate-y-0 flex-col rounded-none p-4 pt-14 md:max-w-none lg:inset-auto lg:left-1/2 lg:top-1/2 lg:h-auto lg:max-h-[90dvh] lg:w-[min(880px,calc(100vw-48px))] lg:max-w-[880px] lg:-translate-x-1/2 lg:-translate-y-1/2 lg:rounded-2xl lg:p-6 lg:pt-10"
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-xl font-bold md:text-2xl">
            <Building05
              className="text-gray-800"
              pathProps={{ stroke: 'currentColor' }}
            />
            Configure your rooms
          </DialogTitle>
          <DialogDescription className="text-start text-sm font-normal text-gray-600">
            {description}
          </DialogDescription>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(280px,320px)] lg:items-start">
              <div className="space-y-3">
                {downtown ? (
                  <HotelAreaPicker
                    event={event}
                    downtown={downtown}
                    value={area}
                    onSelectArea={setArea}
                  />
                ) : null}
                <div>
                  <StayDateRangeField
                    event={event}
                    startDate={checkIn}
                    endDate={checkOut}
                    onChange={({ from, to }) => {
                      setCheckIn(from);
                      setCheckOut(to);
                      setError('');
                    }}
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    {nights > 0
                      ? `You'll stay ${countLabel(nights, 'night', 'nights')}.`
                      : 'Choose your check-in and check-out dates.'}
                  </p>
                </div>
                {rooms.map((room, index) => (
                  <RoomCard
                    key={index}
                    room={room}
                    index={index}
                    canRemove={rooms.length > 1}
                    error={error}
                    onRemove={() =>
                      setRooms((current) =>
                        current.filter((_, roomIndex) => roomIndex !== index)
                      )
                    }
                    onUpdate={(update) => updateRoom(index, update)}
                  />
                ))}
                <button
                  type="button"
                  disabled={rooms.length >= 8}
                  title={
                    rooms.length >= 8
                      ? 'You can add up to 8 rooms'
                      : 'Add another room'
                  }
                  className="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl border border-gray-200 text-sm font-semibold text-primary-500 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={() =>
                    setRooms((current) => [
                      ...current,
                      { adults: 1, children: [], infants: 0 },
                    ])
                  }
                >
                  + Add another room
                </button>
              </div>
              <SearchSummary
                checkIn={checkIn}
                checkOut={checkOut}
                nights={nights}
                totals={totals}
                roomCount={rooms.length}
              />
            </div>
          </div>
          {error ? (
            <p role="alert" className="pt-3 text-sm text-red-600">
              {error}
            </p>
          ) : null}
          <div className="flex flex-shrink-0 flex-col-reverse gap-3 border-t pb-[env(safe-area-inset-bottom)] pt-4 lg:flex-row lg:justify-end">
            <Link
              className="min-h-12 w-full lg:w-auto"
              href={`${links.checkout}?eventId=${eventId}&eventLink=${eventLink}&packageType=${packageType}`}
              onClick={() => {
                useCheckoutStepsStore.setState({ hotelSelected: false });
                if (skipPackageComponent(storage, 'hotel')) {
                  trackGa4PackageComponentRemoved({
                    component: 'hotel',
                    params: {
                      event_id: eventId,
                      package_type:
                        packageType === 'packages' ? 'package' : 'tickets_only',
                    },
                  });
                }
                setCookie(CHECKOUT_START_DATETIME, new Date().getTime());
                storage.setItem(
                  SOCIAL_LOGIN_REDIRECT,
                  `${links.checkout}?eventId=${eventId}&eventLink=${eventLink}&packageType=${packageType}`
                );

                onClose();
              }}
            >
              <Button
                type="button"
                variant="secondary"
                className="min-h-12 w-full lg:w-auto"
              >
                I don't need a hotel
              </Button>
            </Link>
            <Button
              type="button"
              onClick={confirm}
              className="min-h-12 w-full lg:w-auto"
            >
              Search hotels
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SearchSummary({
  checkIn,
  checkOut,
  nights,
  totals,
  roomCount,
}: {
  checkIn?: Date;
  checkOut?: Date;
  nights: number;
  totals: { adults: number; children: number; infants: number };
  roomCount: number;
}) {
  return (
    <aside
      aria-label="Your search summary"
      className="rounded-xl border border-gray-200 bg-white p-4 lg:sticky lg:top-0"
    >
      <p className="mb-3 text-sm font-semibold text-gray-800">
        Your search summary
      </p>
      <div className="space-y-3">
        <div>
          <p className="text-sm font-semibold text-gray-800">
            {checkIn && checkOut
              ? `${formatStayDate(checkIn)} – ${formatStayDate(checkOut)}`
              : 'No dates selected'}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {nights > 0
              ? `You'll stay ${countLabel(nights, 'night', 'nights')}.`
              : 'Choose your check-in and check-out dates.'}
          </p>
        </div>
        <div className="space-y-2.5 border-t border-gray-200 pt-3">
          <SummaryRow
            icon={
              <Users01
                size="16"
                className="text-gray-500"
                pathProps={{ stroke: 'currentColor' }}
              />
            }
          >
            {countLabel(totals.adults, 'Adult', 'Adults')}
          </SummaryRow>
          <SummaryRow
            icon={
              <User01
                size="16"
                className="text-gray-500"
                pathProps={{ stroke: 'currentColor' }}
              />
            }
          >
            {countLabel(totals.children, 'Child', 'Children')}
          </SummaryRow>
          <SummaryRow
            icon={
              <User01
                size="16"
                className="text-gray-500"
                pathProps={{ stroke: 'currentColor' }}
              />
            }
          >
            {countLabel(totals.infants, 'Infant', 'Infants')}
          </SummaryRow>
          <SummaryRow
            icon={
              <Building05
                size="16"
                className="text-gray-500"
                pathProps={{ stroke: 'currentColor' }}
              />
            }
          >
            {countLabel(roomCount, 'Room', 'Rooms')}
          </SummaryRow>
        </div>
        <div className="flex gap-2 border-t border-gray-200 pt-3 text-xs text-gray-500">
          <InfoCircle
            size="16"
            className="mt-0.5 shrink-0 text-gray-400"
            pathProps={{ stroke: 'currentColor' }}
          />
          <p>Prices and availability are based on this configuration.</p>
        </div>
      </div>
    </aside>
  );
}

function SummaryRow({
  icon,
  children,
}: {
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2.5 text-sm text-gray-700">
      {icon}
      <span>{children}</span>
    </div>
  );
}

function RoomCard({
  room,
  index,
  canRemove,
  error,
  onRemove,
  onUpdate,
}: {
  room: RoomDraft;
  index: number;
  canRemove: boolean;
  error: string;
  onRemove: () => void;
  onUpdate: (update: Partial<RoomDraft>) => void;
}) {
  const roomNumber = index + 1;
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <h3 className="font-semibold text-gray-800">Room {roomNumber}</h3>
        <button
          type="button"
          aria-label={`Remove room ${roomNumber}`}
          title={
            canRemove
              ? `Remove room ${roomNumber}`
              : 'You need at least one room'
          }
          disabled={!canRemove}
          className="rounded p-1 text-gray-500 hover:text-gray-800 disabled:opacity-40"
          onClick={onRemove}
        >
          <Trash02 size="20" />
        </button>
      </div>
      <div className="divide-y divide-gray-200 px-4">
        <div className="py-3">
          <GuestCounter
            label="Adults"
            description="Age over 17"
            roomNumber={roomNumber}
            value={room.adults}
            min={1}
            onChange={(adults) => onUpdate({ adults })}
          />
        </div>
        <div className="py-3">
          <GuestCounter
            label="Children"
            description="Age 2–17"
            roomNumber={roomNumber}
            value={room.children.length}
            min={0}
            onChange={(count) =>
              onUpdate({
                children:
                  count > room.children.length
                    ? [...room.children, null]
                    : room.children.slice(0, count),
              })
            }
          />
          {room.children.map((age, childIndex) => (
            <div
              key={childIndex}
              className="mt-3 flex flex-col gap-1.5 text-sm"
            >
              <span className="text-xs font-medium text-gray-700">
                Child {childIndex + 1} age at check-in
              </span>
              <Select
                value={age !== null && age !== undefined ? String(age) : ''}
                onValueChange={(val) =>
                  onUpdate({
                    children: room.children.map((current, i) =>
                      i === childIndex
                        ? val === ''
                          ? null
                          : Number(val)
                        : current
                    ),
                  })
                }
              >
                <SelectTrigger
                  className={cn(
                    'h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm',
                    error && age === null && 'border-red-500'
                  )}
                  aria-label={`Room ${roomNumber}, child ${childIndex + 1} age at check-in`}
                  aria-invalid={Boolean(error && age === null)}
                >
                  <SelectValue placeholder="Select age" />
                </SelectTrigger>
                <SelectContent className="z-[60] max-h-56 bg-white">
                  {Array.from({ length: 16 }, (_, i) => i + 2).map(
                    (ageOption) => (
                      <SelectItem key={ageOption} value={String(ageOption)}>
                        {ageOption}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
        <div className="py-3">
          <GuestCounter
            label="Infants"
            description="Age under 2"
            roomNumber={roomNumber}
            value={room.infants}
            min={0}
            onChange={(infants) => onUpdate({ infants })}
          />
        </div>
      </div>
    </div>
  );
}

function GuestCounter({
  label,
  description,
  roomNumber,
  value,
  min,
  onChange,
}: {
  label: string;
  description: string;
  roomNumber: number;
  value: number;
  min: number;
  onChange: (value: number) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div>
        <p className="font-medium text-gray-800">{label}</p>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 text-lg leading-none text-gray-700 disabled:opacity-40"
          disabled={value <= min}
          aria-label={`Remove ${label.toLowerCase()} from room ${roomNumber}`}
          onClick={() => onChange(value - 1)}
        >
          −
        </button>
        <span className="min-w-5 text-center text-sm font-semibold">
          {value}
        </span>
        <button
          type="button"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 text-lg leading-none text-gray-700"
          aria-label={`Add ${label.toLowerCase()} to room ${roomNumber}`}
          onClick={() => onChange(value + 1)}
        >
          +
        </button>
      </div>
    </div>
  );
}
